import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TemplatePageModel, TemplatePageRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['templatePages']

type TemplatePageFilters = {
  active?: 'true' | 'false',
  agency_id?: number,
  brand_id?: number,
  brand_theme_id?: number,
  category_id?: number,
  not_assigned_to_agency_id?: number,
  provider_id?: number,
  publication_id?: number,
  service_id?: number,
  service_product_id?: number,
  string?: string,
  tag_id?: number,
}

type UseTemplatePagesOptions = {
  filters?: TemplatePageFilters,
  performHttpRequests?: boolean,
  requestOptions?: TemplatePageRequestOptions,
}

function useTemplatePages(options: UseTemplatePagesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    active: filterActive,
    agency_id: filterAgencyId,
    brand_id: filterBrandId,
    brand_theme_id: filterBrandThemeId,
    category_id: filterCategoryId,
    not_assigned_to_agency_id: filterNotAssignedToAgencyId,
    provider_id: filterProviderId,
    publication_id: filterPublicationId,
    service_id: filterServiceId,
    service_product_id: filterServiceProductId,
    string: filterString,
    tag_id: filterTagId,
  } = filters

  const {
    updatedEntities: { templatePages: templatePagesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { templatePages } = useSelector(reduxState => reduxState.entities)

  const filteredTemplatePages = useMemo(() => {
    const filtered = Object.values(templatePages).filter((templatePage: TemplatePageModel) => {
      const {
        active, brand_id, categories = [], id, permission_ids, service_id, tags, title,
      } = templatePage

      const {
        agencies, brand_themes, products, providers, publications,
      } = permission_ids || {}

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchAgencyId = filterAgencyId ? matchFilterArrayIncludes(agencies, filterAgencyId) : true
      const matchBrandId = matchFilterNumber(brand_id, filterBrandId)
      const matchPublicationId = filterPublicationId
        ? matchFilterArrayIncludes(publications, filterPublicationId) : true

      const matchBrandThemeId = filterBrandThemeId
        ? matchFilterArrayIncludes(brand_themes, filterBrandThemeId)
        : true
      const matchCategoryId = filterCategoryId ? matchFilterArrayIncludes(categories, Number(filterCategoryId)) : true
      const matchNotAssignedToAgencyId = filterNotAssignedToAgencyId
        ? !matchFilterArrayIncludes(agencies, filterNotAssignedToAgencyId)
        : true
      const matchProviderId = filterProviderId ? matchFilterArrayIncludes(providers, filterProviderId) : true
      const matchServiceId = matchFilterNumber(service_id, filterServiceId)
      const matchServiceProductId = filterServiceProductId
        ? matchFilterArrayIncludes(products, filterServiceProductId)
        : true
      const matchTagId = filterTagId ? matchFilterArrayIncludes(tags, Number(filterTagId)) : true

      const matchActive = filterActive ? filterActive === String(active) : true

      return matchActive
        && matchAgencyId
        && matchBrandId
        && matchBrandThemeId
        && matchCategoryId
        && matchNotAssignedToAgencyId
        && matchProviderId
        && matchPublicationId
        && matchServiceId
        && matchServiceProductId
        && matchString
        && matchTagId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [templatePagesUpdatedAt, JSON.stringify(filters)])

  const filteredTemplatePagesCount = filteredTemplatePages.length
  const hasFilteredTemplatePages = !!filteredTemplatePagesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTemplatePagesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTemplatePages } = useReduxAction(
    'templatePages',
    'loadTemplatePages',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading: loadingTemplatePages,
  }
}

export default useTemplatePages
